import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import _get from 'lodash/get';
import { GraphQLFormattedError } from 'graphql';
import {
  AiDocumentationFieldEnum,
  Tag,
  useEdmDocumentationCategoryLazyQuery,
} from 'graphql-common';
import { SelectOption } from '@lib/components/Select/types';
import { OnCreateTagFn } from '@lib/components/TagsInput/types';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import { AiFieldUiState } from 'routes/Documentation/utils/getAiFieldDetectionState';
import AiFieldAssist, {
  AiFieldAssistLabels,
} from 'routes/Documentation/components/AiDetection/AiFieldAssist';
import resolveAiFormSource from '../../utils/resolveAiFormSource';
import setCategoryWithPermissions from '../../utils/setCategoryWithPermissions';

type Props = {
  field: AiDocumentationFieldEnum;
  targetFieldName: string;
  labels: AiFieldAssistLabels;
  isEdit?: boolean;
  documentationVersionId?: string;
  onCreateTag?: OnCreateTagFn;
  creditsRemaining?: AiCredits | null;
  detectionUiState?: AiFieldUiState;
  suggestedCodes?: string[];
  detectingLabel?: string;
  onDismiss?: () => void;
};

// Thin react-hook-form adapter around the shared, form-agnostic AiFieldAssist
// (Task 1): resolves the generation source from form state (a saved version
// to regenerate from in edit mode, or the freshly-uploaded attachment in
// create mode) and applies a completed generation via setValue / category
// lookup / tag link-or-create — exactly what AiFieldAssist itself used to do
// internally before it was decoupled for reuse on the List/View surfaces.
export default function AiFormFieldAssist({
  field,
  targetFieldName,
  isEdit,
  documentationVersionId,
  onCreateTag,
  ...rest
}: Props) {
  const { getValues, setValue } = useFormContext();

  // CATEGORY generation returns a bare id (`state.categoryId`) — resolve it to
  // a {value, label, color, …permission defaults} option (the shape
  // ChipValueContainer/ChipOption expect, matching
  // getDocumentationCategoriesLoadOptions' own mapping) before writing it
  // into the Select field, prefilling the permission checkboxes with it.
  const [fetchCategory, { data: categoryData }] =
    useEdmDocumentationCategoryLazyQuery({
      fetchPolicy: 'no-cache',
    });

  // Derived side-effect from `data` (React 19 pattern) replaces the deprecated
  // `onCompleted` option. Apply each fetched result once — the effect can
  // re-run for the same `categoryData` and must not keep re-writing the field.
  const appliedCategoryDataRef = useRef<typeof categoryData>(undefined);
  useEffect(() => {
    const category = categoryData?.data;
    if (!category || appliedCategoryDataRef.current === categoryData) return;
    appliedCategoryDataRef.current = categoryData;
    setCategoryWithPermissions(setValue, category, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [categoryData, setValue]);

  const resolveSource = () =>
    resolveAiFormSource({ getValues, isEdit, documentationVersionId });

  const onApplyValue = (value: string) => {
    setValue(targetFieldName, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  // Lets AiFieldAssist detect a race with the user editing the field while a
  // generation is in flight (see its getCurrentValue doc comment). CATEGORY's
  // form value is a {value, label, color} option, not a bare id — compare on
  // its `value` to match the id a CATEGORY generation carries.
  const isCategoryField = field === AiDocumentationFieldEnum.Category;
  const getCurrentValue = (): string => {
    const value = getValues(targetFieldName);
    if (isCategoryField) {
      const categoryId = (value as SelectOption | null)?.value;
      return categoryId ? String(categoryId) : '';
    }
    return (value as string) ?? '';
  };

  const getTagsValue = (): SelectOption[] =>
    (getValues(targetFieldName) as SelectOption[]) || [];

  // Append a resolved tag to the Tags field, deduped against the current value.
  const appendTag = (id: string, label: string) => {
    const current = getTagsValue();
    const exists = current.some(
      (option) => option?.value === id || option?.label === label,
    );
    if (exists) return;
    setValue(targetFieldName, [...current, { value: id, label }], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  // Create-or-link one generated name, then append the resolved tag. Mirrors
  // TagsInput's create/"taken" handling so the result is identical to the user
  // having typed the tag in.
  const linkTag = (name: string) => {
    if (!onCreateTag) return;
    onCreateTag(
      name,
      (tag: Tag | null) => {
        if (tag) appendTag(tag.id, tag.name);
      },
      (errors: readonly GraphQLFormattedError[]) => {
        const meta = _get(
          errors,
          [0, 'extensions', 'details', 0, 'meta', 'tag'],
          {},
        ) as { id?: string; reason?: string };
        if (meta.reason === 'taken' && meta.id) appendTag(meta.id, name);
        // Any other failure is surfaced by onCreateTag's own error handling.
      },
    );
  };

  // Link-or-create every generated tag name into the Tags field, skipping names
  // already selected (case-insensitive) and de-duping within the generated list.
  const onApplyTagNames = (values: string[]) => {
    const selectedLabels = new Set(
      getTagsValue().map((option) => String(option?.label ?? '').toLowerCase()),
    );
    values.forEach((name) => {
      const key = name?.toLowerCase();
      if (key && !selectedLabels.has(key)) {
        selectedLabels.add(key);
        linkTag(name);
      }
    });
  };

  const onApplyCategoryId = (categoryId: string) => {
    fetchCategory({ variables: { id: categoryId } });
  };

  return (
    <AiFieldAssist
      field={field}
      resolveSource={resolveSource}
      onApplyValue={onApplyValue}
      onApplyTagNames={onApplyTagNames}
      onApplyCategoryId={onApplyCategoryId}
      getCurrentValue={getCurrentValue}
      {...rest}
    />
  );
}
